<template>
    <b-popover
        :custom-class="`filter-popover ${customPosition}`"
        :triggers="triggers"
        :target="target"
        :placement="placement"
        :offset="offset"
        :show.sync="show"
        boundary="viewport"
        @show="onShow"
        @hidden="onHidden"
    >
        <div class="header-filter">
            <div class="title">{{ $t("CalendarsPage.Filters") }}</div>
            <sys-button
                scheme="square"
                variant="outline"
                size="xs"
                @click="clearAllFilters"
                noBorder
                class="clear-button"
            >
                {{ $t("Operator.Clean") }}
            </sys-button>
        </div>
        <div class="select-filter-type">
            <div
                @click="handleStandardTab"
                :class="['type', currentFilter === 'standard' ? '--is-active' : '']"
            >
                {{ $t("Breadcrumbs.Standard") }}
            </div>
            <div
                @click="handleCustomTab"
                :class="['type', currentFilter === 'custom' ? '--is-active' : '']"
            >
                {{ $t("CalendarsPage.Customized") }}
            </div>
        </div>

        <div
            v-if="currentFilter === 'standard'"
            class="standard-block"
        >
            <div class="months">
                <div class="title">
                    {{
                        `${$t("CalendarsPage.Month")} ${
                            selectedMonths.length > 0 ? `(${selectedMonths.length})` : ""
                        }`
                    }}
                </div>
                <div class="carousel">
                    <button
                        class="carousel-control left"
                        @click="moveMonth('left')"
                    >
                        <Arrow />
                    </button>
                    <div class="month-list">
                        <div
                            v-for="(month, index) in visibleMonths"
                            :key="index"
                            :class="[
                                'month-item',
                                selectedMonths.includes(month) ? '--is-active' : ''
                            ]"
                            @click="toggleMonth(month)"
                        >
                            {{ month }}
                        </div>
                    </div>

                    <button
                        class="carousel-control right"
                        @click="moveMonth('right')"
                    >
                        <Arrow />
                    </button>
                </div>
            </div>
            <div class="years">
                <div class="title">
                    {{
                        `${$t("CalendarsPage.Year")} ${selectedYears.length > 0 ? `(${selectedYears.length})` : ""}`
                    }}
                </div>
                <div class="carousel">
                    <button
                        class="carousel-control left"
                        @click="moveYear('left')"
                    >
                        <Arrow />
                    </button>
                    <div class="year-list">
                        <div
                            v-for="(year, index) in visibleYears"
                            :key="index"
                            :class="[
                                'year-item',
                                selectedYears.includes(year) ? '--is-active' : ''
                            ]"
                            @click="toggleYear(year)"
                        >
                            {{ year }}
                        </div>
                    </div>

                    <button
                        class="carousel-control right"
                        @click="moveYear('right')"
                    >
                        <Arrow />
                    </button>
                </div>
            </div>
        </div>
        <div
            v-else
            class="custom-block"
        >
            <div class="datepicker-block">
                <sys-form-datepicker
                    id="from-day"
                    type="date"
                    :label="$t('CalendarsPage.From')"
                    :value="startDate"
                    @input="(value) => (startDate = value)"
                />
                <sys-form-datepicker
                    id="to-day"
                    type="date"
                    :label="$t('CalendarsPage.To')"
                    :value="endDate"
                    @input="(value) => (endDate = value)"
                />
            </div>
            <div class="button-block">
                <sys-button
                    scheme="square"
                    variant="outline"
                    size="xs"
                    full
                    @click="() => setToday()"
                >
                    {{ $t("CalendarsPage.Today") }}
                </sys-button>
                <sys-button
                    scheme="square"
                    variant="outline"
                    size="xs"
                    full
                    @click="() => setThisWeek()"
                >
                    {{ $t("CalendarsPage.ThisWeek") }}
                </sys-button>
                <sys-button
                    scheme="square"
                    variant="outline"
                    size="xs"
                    full
                    @click="() => setThisMonth()"
                >
                    {{ $t("CalendarsPage.ThisMonth") }}
                </sys-button>
                <sys-button
                    scheme="square"
                    variant="outline"
                    size="xs"
                    full
                    @click="() => setThisYear()"
                >
                    {{ $t("CalendarsPage.ThisYear") }}
                </sys-button>
            </div>
        </div>
        <div class="footer-filter">
            <sys-button
                scheme="square"
                variant="contained"
                size="xs"
                @click="applyFilters"
                full
            >
                {{ `${$t("CalendarsPage.ApplyFilters")} ${getTotalSelectedFilters()} ` }}
            </sys-button>
        </div>
    </b-popover>
</template>

<script>
    import { BPopover } from "bootstrap-vue";
    import { mapMutations, mapState } from "vuex";
    import SysButton from "@/@core/components/sys-button";
    import SysFormDatepicker from "@/@core/components/sys-form-datepicker";
    import Arrow from "@core/assets/icons/arrow-right.svg";

    export default {
        components: { BPopover, SysButton, SysFormDatepicker, Arrow },
        props: {
            title: { type: String, required: false },
            placement: {
                type: String,
                default: "right"
            },
            triggers: {
                type: String,
                default: "focus"
            },
            target: {
                type: String,
                required: true
            },
            offset: {
                type: Number,
                default: 0
            },
            customPosition: {
                type: String,
                default: null
            },
            windowWidth: {
                type: Number,
                default: null
            }
        },
        data() {
            return {
                show: false,
                currentFilter: "standard",
                selectedMonths: [],
                selectedYears: [],
                currentMonthIndex: 0,
                currentYearIndex: 0,
                itemsPerPage: 4,
                startDate: null,
                endDate: null
            };
        },
        mounted() {},
        methods: {
            ...mapMutations("calendar", ["UPDATE_FILTERS_EXCEPTION"]),
            onShow() {
                this.setSelectedFilters();
                this.setInitialYearPosition();
                this.$emit("onShow");
            },
            onHidden() {
                this.currentFilter = "standard";
                this.clearUnAppliedFilters();
                this.$emit("onHidden");
            },
            handleStandardTab() {
                this.currentFilter = "standard";

                this.startDate = null;
                this.endDate = null;
            },
            handleCustomTab() {
                this.currentFilter = "custom";

                this.selectedMonths = [];
                this.selectedYears = [];
            },
            setInitialYearPosition() {
                const currentYear = new Date().getFullYear();
                const indexOfCurrentYear = this.helpers.context.years.indexOf(currentYear);

                if (indexOfCurrentYear !== -1) {
                    const halfPage = Math.floor(this.itemsPerPage / 2);
                    this.currentYearIndex = Math.max(indexOfCurrentYear - halfPage, 0);
                }
            },
            clearUnAppliedFilters() {
                this.selectedMonths = this.selectedMonths.filter((month) =>
                    this.exception.filters.some((filter) => filter.value == month)
                );

                this.selectedYears = this.selectedYears.filter((year) =>
                    this.exception.filters.some((filter) => filter.value == year.toString())
                );

                const dateRange =
                    this.startDate && this.endDate
                        ? `${this.formatDate(this.startDate)} ${this.$t("CalendarsPage.to")} ${this.formatDate(this.endDate)}`
                        : null;

                if (!this.exception.filters.some((filter) => filter.value == dateRange)) {
                    this.startDate = null;
                    this.endDate = null;
                }
            },
            setSelectedFilters() {
                const filters = this.exception.filters;

                filters.forEach(({ type, value }) => {
                    if (this.translatedMonths.includes(value)) {
                        this.selectedMonths.push(value);
                        return;
                    }

                    if (this.helpers.context.years.includes(Number(value))) {
                        this.selectedYears.push(Number(value));
                        return;
                    }

                    if (type == "manual-custom") {
                        const splittedDate = value.split(" ");

                        this.startDate = new Date(this.$parseDate(splittedDate[0], "DMY"));
                        this.endDate = new Date(this.$parseDate(splittedDate.at(-1), "DMY"));
                    }
                });
            },
            clearAllFilters() {
                this.selectedMonths = [];
                this.selectedYears = [];
                this.startDate = null;
                this.endDate = null;
                this.closePopover(false);

                this.clearUnAppliedFilters();
                this.UPDATE_FILTERS_EXCEPTION([]);
            },
            closePopover(e) {
                e ? (this.show = e) : (this.show = false);
            },
            getTotalSelectedFilters() {
                const totalFilters = this.selectedMonths.length + this.selectedYears.length;
                return totalFilters > 0 ? `(${totalFilters})` : "";
            },
            moveMonth(direction) {
                const maxIndex = this.translatedMonths.length - this.itemsPerPage;

                if (direction === "left") {
                    if (this.currentMonthIndex === 0) {
                        this.currentMonthIndex = maxIndex;
                    } else {
                        this.currentMonthIndex = Math.max(
                            this.currentMonthIndex - this.itemsPerPage,
                            0
                        );
                    }
                } else {
                    if (this.currentMonthIndex >= maxIndex) {
                        this.currentMonthIndex = 0;
                    } else {
                        this.currentMonthIndex = Math.min(
                            this.currentMonthIndex + this.itemsPerPage,
                            maxIndex
                        );
                    }
                }
            },
            moveYear(direction) {
                const maxIndex = this.helpers.context.years.length - this.itemsPerPage;

                if (direction === "left") {
                    if (this.currentYearIndex === 0) {
                        this.currentYearIndex = maxIndex;
                    } else {
                        this.currentYearIndex = Math.max(
                            this.currentYearIndex - this.itemsPerPage,
                            0
                        );
                    }
                } else {
                    if (this.currentYearIndex >= maxIndex) {
                        this.currentYearIndex = 0;
                    } else {
                        this.currentYearIndex = Math.min(
                            this.currentYearIndex + this.itemsPerPage,
                            maxIndex
                        );
                    }
                }
            },
            selectMonth(month) {
                this.currentMonth = month;
            },
            selectYear(year) {
                this.currentYear = year;
            },
            toggleMonth(month) {
                const index = this.selectedMonths.indexOf(month);
                if (index === -1) {
                    this.selectedMonths.push(month);
                } else {
                    this.selectedMonths.splice(index, 1);
                }
            },
            toggleYear(year) {
                const index = this.selectedYears.indexOf(year);
                if (index === -1) {
                    this.selectedYears.push(year);
                } else {
                    this.selectedYears.splice(index, 1);
                }
            },
            handleFooterButtonSize() {
                if (this.windowWidth > 768) {
                    return "md";
                }

                if (this.windowWidth > 480 && this.windowWidth <= 768) {
                    return "md";
                }

                if (this.windowWidth <= 480) {
                    return "xs";
                }
            },
            setToday() {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const ISO = today.toISOString();
                this.startDate = ISO;
                this.endDate = ISO;

                this.applyFilters(true);
            },
            setThisWeek() {
                const today = new Date();
                const firstDay = new Date(today.setDate(today.getDate() - today.getDay() + 1)) // Segunda-feira
                    .toISOString()
                    .split("T")[0];
                const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 7)) // Domingo
                    .toISOString()
                    .split("T")[0];
                this.startDate = firstDay;
                this.endDate = lastDay;
                this.applyFilters(true);
            },
            setThisMonth() {
                const today = new Date();
                const start = new Date(today.getFullYear(), today.getMonth(), 1);
                const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

                this.startDate = start;
                this.endDate = end;
                this.applyFilters(true);
            },

            setThisYear() {
                const today = new Date();
                const start = new Date(today.getFullYear(), 0, 1);
                const end = new Date(today.getFullYear(), 11, 31);

                this.startDate = start;
                this.endDate = end;
                this.applyFilters(true);
            },
            applyFilters(isSingleFilter = false) {
                const isCustomDate = this.startDate && this.endDate;

                const filters = [];

                if (isSingleFilter || isCustomDate) {
                    const formattedDate = `${this.formatDate(this.startDate)} ${this.$t("CalendarsPage.to")} ${this.formatDate(this.endDate)}`;

                    this.clearAllFilters();
                    this.UPDATE_FILTERS_EXCEPTION([
                        {
                            type: isSingleFilter ? "custom" : "manual-custom",
                            value: formattedDate
                        }
                    ]);

                    return;
                }

                if (this.selectedMonths.length > 0) {
                    filters.push(
                        ...this.selectedMonths.map((month) => ({
                            type: "standard",
                            value: month
                        }))
                    );
                }

                if (this.selectedYears.length > 0) {
                    filters.push(
                        ...this.selectedYears.map((year) => ({
                            type: "standard",
                            value: String(year)
                        }))
                    );
                }

                this.clearAllFilters();
                this.UPDATE_FILTERS_EXCEPTION([...new Set(filters)]);
            },
            formatDate(date) {
                const d = new Date(date);
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const year = d.getFullYear();
                return `${day}/${month}/${year}`;
            }
        },
        computed: {
            ...mapState("calendar", {
                exception: "exception",
                helpers: "helpers"
            }),
            visibleMonths() {
                return this.translatedMonths.slice(
                    this.currentMonthIndex,
                    this.currentMonthIndex + this.itemsPerPage
                );
            },
            visibleYears() {
                return this.helpers.context.years.slice(
                    this.currentYearIndex,
                    this.currentYearIndex + this.itemsPerPage
                );
            },
            translatedMonths() {
                const userInfo = this.$cookies.get("userInfo");
                const language = userInfo?.language || "pt-BR";

                const translations = {
                    "en-US": [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec"
                    ],
                    "pt-BR": [
                        "Jan",
                        "Fev",
                        "Mar",
                        "Abr",
                        "Mai",
                        "Jun",
                        "Jul",
                        "Ago",
                        "Set",
                        "Out",
                        "Nov",
                        "Dez"
                    ]
                };

                return translations[language] || translations["en-US"];
            }
        }
    };
</script>

<style lang="scss">
    .filter-popover {
        background: #fff;
        border-radius: 6px;
        box-shadow: 0px 5px 25px 0px rgba(0, 0, 0, 0.1);
        max-width: 345px;
        width: 100%;

        .arrow {
            display: none !important;
        }

        .popover-body {
            padding: 0 !important;
            width: inherit !important;
            border: none !important;
        }

        .header-filter {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            padding: 16px 16px 15px;
            border-bottom: 1px solid #cfc4be;

            .title {
                color: #4c4541;
                font-size: 16px;
                font-weight: 600;
                line-height: 26px;
            }

            button {
                height: 28px;
            }
        }

        .select-filter-type {
            display: flex;
            align-items: center;

            .type {
                cursor: pointer;
                text-align: center;
                width: 100%;
                padding-block: 16px;
                color: #998f8a;
                font-size: 16px;
                font-weight: 600;
                line-height: 26px;

                &.--is-active {
                    color: #974900;
                    box-shadow: 0px -4px 0px -1px #974900 inset;
                }
            }
        }

        .standard-block {
            .months,
            .years {
                display: flex;
                flex-direction: column;
                gap: 8px;
                padding: 16px;

                .title {
                    color: #4c4541;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 20px;
                }

                .carousel {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    gap: 8px;

                    .carousel-control {
                        background: transparent;
                        border: none;
                        cursor: pointer;
                        padding: 8px;
                        border-radius: 5px;
                        height: 28px;
                        display: flex;
                        align-items: center;

                        &.right,
                        &.left {
                            svg {
                                width: 12px;
                                height: 12px;
                                fill: #974900;
                            }
                        }

                        &.left {
                            svg {
                                transform: rotate(180deg);
                            }
                        }

                        &:hover {
                            background-color: #ffdbc4;
                        }
                    }

                    .month-list,
                    .year-list {
                        display: flex;
                        overflow: hidden;
                        justify-content: center;
                        gap: 8px;
                    }

                    .month-item,
                    .year-item {
                        cursor: pointer;
                        border-radius: 8px;
                        background-color: #fff;
                        border: 1px solid #974900;
                        display: flex;
                        width: 54px;
                        padding: 5px 12px;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;
                        color: #974900;
                        text-align: center;
                        font-size: 12px;
                        font-weight: 600;
                        line-height: 20px;
                        letter-spacing: 0.15px;

                        &.--is-active {
                            background-color: #ffdbc4;
                            border-color: transparent;
                        }

                        &:hover {
                            background-color: #ffdbc4;
                        }
                    }
                }
            }
        }

        .custom-block {
            .datepicker-block {
                display: flex;
                gap: 16px;
                padding: 16px;
            }

            .button-block {
                display: flex;
                flex-direction: column;
                gap: 8px;
                padding: 0 16px 16px;
            }
        }

        .footer-filter {
            padding: 15px 16px 16px;
            border-top: 1px solid #cfc4be;
        }
    }
</style>
